import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { breakpoint as breakpoints } from '@op/opds.tokens';

/**
 * React hook to merge multiple React refs (either MutableRefObjects or ref callbacks) into a single ref callback that
 * updates all provided refs
 * @param refs - Refs to collectively update with one ref value.
 * @returns A function with an attached "current" prop, so that it can be treated like a RefObject.
 * https://github.com/microsoft/fluentui/blob/master/packages/react-hooks/src/useMergedRefs.ts
 */

export function useCombinedRefs() {
  for (var _len = arguments.length, refs = new Array(_len), _key = 0; _key < _len; _key++) {
    refs[_key] = arguments[_key];
  }
  // useCallback gives more fine-grain control over when refs are set and unset
  const combinedRef = useCallback(value => {
    // to update the outter ref with the inner ref, pass a value to the callback()
    combinedRef.current = value;

    // if each ref != undefined or null, iterate through refs[]
    refs.filter(Boolean).forEach(ref => {
      if (typeof ref === 'function') {
        ref(value);
      } else {
        // overwrite the immutability of the React.Ref type by assigning the desired value to it
        ref.current = value;
      }
    });
  }, [...refs]);
  return combinedRef;
}

/**
 * React hook for an element's current focus state
 *
 * @param {RefObject} ref - A reference to a DOM element
 * @param {boolean | undefined} force - If `true`, force the returned state
 * @returns {boolean}
 */

export function useFocus(ref, force) {
  const [state, setState] = useState(false);
  useEffect(() => {
    var _ref$current, _ref$current2;
    if (typeof force === 'boolean') {
      if (force !== state) {
        setState(force);
      }
      return;
    }
    const onFocus = () => setState(true);
    const onBlur = () => setState(false);
    (_ref$current = ref.current) == null || _ref$current.addEventListener('focus', onFocus);
    (_ref$current2 = ref.current) == null || _ref$current2.addEventListener('blur', onBlur);
    return () => {
      if (!ref.current) {
        return;
      }
      ref.current.removeEventListener('focus', onFocus);
      ref.current.removeEventListener('blur', onBlur);
    };
  }, [force]);
  return state;
}

/**
 * React hook always returning the previous value with which it was called.
 * Initially `undefined`.
 *
 * @param {*} value - The value being tracked
 * @returns {*} - The previous value
 */
export function usePrevious(value) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

/**
 * React hook to test if given breakpoint matches current window size.
 *
 * @param {string} breakpoint - Breakpoint token
 * @returns {boolean} - Breakpoint matches
 */
// TODO: Change "breakpoint: string" to "breakpoint: Breakpoint" when the type is exported from the tokens package. https://oppjira.eficode.com/browse/OPDS-6205
export function useBreakpoint(breakpoint, mobileFirst) {
  if (mobileFirst === void 0) {
    mobileFirst = true;
  }
  const [match, setMatch] = useState(!mobileFirst);
  const [initialised, setInitialised] = useState(false);
  const testBreakpoint = () => {
    // @ts-expect-error -- TODO: Remove this comment when the breakpoint is typed as Breakpoint that is exported from the tokens package. https://oppjira.eficode.com/browse/OPDS-6205
    if (!breakpoints[breakpoint]) {
      if (process.env.NODE_ENV !== 'production') {
        console.error("Attempting to access non-existent breakpoint token: " + breakpoint);
      }
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }
    setInitialised(true);

    // @ts-expect-error -- TODO: Remove this comment when the breakpoint is typed as Breakpoint that is exported from the tokens package. https://oppjira.eficode.com/browse/OPDS-6205
    const {
      max,
      min
    } = breakpoints[breakpoint];
    const baseFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const emToPxRatio = baseFontSize || 16; // Breakpoints are defined in em units

    setMatch(mobileFirst ? !min || parseFloat(min) * emToPxRatio <= window.innerWidth : !max || window.innerWidth <= parseFloat(max) * emToPxRatio);
  };
  useEffect(() => {
    // Initial test.
    testBreakpoint();
  }, [breakpoint]);
  useEffect(() => {
    if (typeof window === 'undefined' || initialised) {
      return;
    }

    // Test on resize with throttling.
    let timeout = null;
    let tick = false;
    const resizeListener = () => {
      if (!tick) {
        timeout = setTimeout(() => {
          testBreakpoint();
          tick = false;
        }, 100);
      }
      tick = true;
    };
    window.addEventListener('resize', resizeListener);
    return () => {
      window.removeEventListener('resize', resizeListener);
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, []);
  return match;
}

/**
 * Effect hook that ignores the first invocation (e.g. on mount). The signature
 * is exactly the same as the useEffect hook.
 *
 * @param {function} effect — Imperative function that can return a cleanup
 * function.
 * @param {array} deps — If present, effect will only activate if the values in
 * the list change.
 */
export const useUpdateEffect = (effect, deps) => {
  const mounted = useRef(false);
  useEffect(() => {
    if (mounted.current) {
      effect();
    } else {
      mounted.current = true;
    }
  }, deps);
};

/**
 * Effect hook that conditionally calls useEffect on the server (no-op) and
 * useLayoutEffect in the browser to avoid warning when server-side rendering.
 *
 * Originally from react-redux
 * https://github.com/reduxjs/react-redux/blob/fa5857281a37545c7c036fb2499159b865b1c57d/src/components/connectAdvanced.js#L40
 */
export const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;